import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Store, Wrench, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("customer");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      setSession(data.token, data.user);
      toast.success("مرحبًا بك 👋");
      const r = data.user.role;
      nav(r === "admin" ? "/admin" : r === "seller" ? "/seller" : "/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-14">
      <div className="text-center mb-8">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--amber)] text-[#0B0F17] mb-3"><Wrench size={28} /></span>
        <h1 className="font-head text-2xl font-black text-white">تسجيل الدخول</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">اختر نوع حسابك للمتابعة</p>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-6">
        <button data-testid="role-switcher-customer" onClick={() => setTab("customer")}
          className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold border ${tab === "customer" ? "border-[var(--amber)] text-[var(--amber)] bg-[var(--amber)]/10" : "border-[var(--border-c)] text-[var(--text-muted)]"}`}>
          <User size={18} /> دخول كعميل
        </button>
        <button data-testid="role-switcher-seller" onClick={() => setTab("seller")}
          className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold border ${tab === "seller" ? "border-[var(--amber)] text-[var(--amber)] bg-[var(--amber)]/10" : "border-[var(--border-c)] text-[var(--text-muted)]"}`}>
          <Store size={18} /> دخول كتشليح
        </button>
      </div>

      <form onSubmit={submit} className="ts-card p-6 space-y-4">
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">البريد الإلكتروني أو رقم الجوال</label>
          <input data-testid="login-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required
            className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none" placeholder="name@email.com أو 05xxxxxxxx" />
        </div>
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">كلمة المرور</label>
          <div className="relative">
            <input data-testid="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 pl-12 text-white outline-none" placeholder="••••••••" />
            <button type="button" data-testid="toggle-login-password" onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--amber)] transition-colors">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="text-left mt-2">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-bold text-[var(--amber)] hover:underline">
              نسيت كلمة المرور؟
            </Link>
          </div>
        </div>
        <button data-testid="login-submit" disabled={loading} className="btn-amber w-full justify-center disabled:opacity-60">
          {loading ? "جارٍ الدخول..." : "دخول"}
        </button>
        {tab === "customer" && (
          <button type="button" onClick={googleLogin} data-testid="google-login" className="w-full flex items-center justify-center gap-2 rounded-xl border border-[var(--border-c)] py-3 text-sm font-bold text-white hover:border-[var(--amber)] transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22 22-9.8 22-22c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 15.4 2 8 6.9 4.3 14z"/><path fill="#4CAF50" d="M24 46c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.6 36.6 26.9 38 24 38c-5.2 0-9.6-3.3-11.3-7.9l-6.6 5.1C9.6 41 16.2 46 24 46z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.6l6.6 5.6C41.4 36.6 46 31 46 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            الدخول عبر Google
          </button>
        )}
      </form>

      <p className="text-center text-sm text-[var(--text-muted)] mt-6">
        {tab === "customer" ? (
          <>ليس لديك حساب؟ <Link to="/register" data-testid="go-register-customer" className="text-[var(--amber)] font-bold">أنشئ حساب عميل</Link></>
        ) : (
          <>تريد تسجيل تشليحك؟ <Link to="/join-seller" data-testid="go-register-seller" className="text-[var(--amber)] font-bold">سجّل تشليحك</Link></>
        )}
      </p>
    </div>
  );
}
